import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import { dashboardCompanyOverviewUrl } from 'dashboard409a/common/config/urls';
import { MODEL_SUBFOLDER, REPORT_SUBFOLDER } from 'dashboard409a/components/OperationsModal/dropbox.constants';
import { buildDropboxPath } from 'dashboard409a/components/OperationsModal/dropbox.utils';
import {
  dropboxRootFromReference,
  toWorkboardRelativePrefix,
} from 'dashboard409a/components/OperationsModal/workboardPath.utils';
import { useFinalReportEmailTemplates } from 'dashboard409a/hooks/useFinalReportEmailTemplates';
import {
  draftFinalReportEmail,
  findScalarOpinion,
  getLatestValuationExtractionRun,
  getReportContacts,
  getReportSelection,
  getValuationExtractionStatus,
  listWorkboardFiles,
  saveReportSelection,
  startValuationExtraction,
  updateScalarOpinion,
} from 'dashboard409a/services/dashboard';
import { Logger } from 'utilities';
import { ProcessReportModalProps } from './ProcessReportModal.props';
import { FileCheckboxItem, FinalReportEmailTemplate } from './ProcessReportModal.types';
import {
  composeFinalReportEmail,
  contactSelectKey,
  deriveDatePrefix,
  fileNameFromPath,
  formatCombinedEmail,
  isTerminalExtractionStatus,
  parseCombinedEmail,
} from './ProcessReportModal.utils';

type UseProcessReportModalParams = Pick<
  ProcessReportModalProps,
  | 'open'
  | 'cmdId'
  | 'valuationId'
  | 'companyName'
  | 'valuationFolderPath'
  | 'existingReportReference'
>;

// Delay before persisting email edits so we save once the analyst pauses typing.
const DRAFT_EMAIL_SAVE_DEBOUNCE_MS = 800;

const useProcessReportModal = ({
  open,
  cmdId,
  valuationId,
  companyName,
  valuationFolderPath,
  existingReportReference,
}: UseProcessReportModalParams) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    templates: emailTemplates,
    presets: emailPresets,
    selectedId: selectedTemplateId,
    setSelectedId: setSelectedTemplateId,
    selectedTemplate,
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
  } = useFinalReportEmailTemplates();

  const [selectedModelPath, setSelectedModelPath] = useState<string | null>(null);
  const [selectedReportPath, setSelectedReportPath] = useState<string | null>(existingReportReference ?? null);
  // Stable Select value: prefer Attio recordId, fall back to email (emails alone can be blank).
  const [selectedContactKey, setSelectedContactKey] = useState<string>('');
  // Subject + body are edited separately but persisted as one combined string.
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [draftDashboardUrl, setDraftDashboardUrl] = useState<string | null>(null);
  const [notesParsed, setNotesParsed] = useState<number | null>(null);
  const [initialPickerPrefix, setInitialPickerPrefix] = useState('');
  const [extractionRunId, setExtractionRunId] = useState<string | null>(null);
  // True once the analyst kicks off a run. The email is drafted immediately and
  // in parallel with the async extraction — we no longer wait for processing.
  const [processInitiated, setProcessInitiated] = useState(false);

  const draftEmail = useMemo(
    () => formatCombinedEmail(draftSubject, draftBody),
    [draftSubject, draftBody]
  );

  // Remember the model/report selections per valuation (server-persisted, keyed by Attio id).
  const hydratedRef = useRef(false);
  const lastSavedRef = useRef<string>('');
  // Ensure we draft only once per initiated run (guards against re-fires when
  // the linked CMD/opinion ids settle after extraction starts).
  const draftInitiatedRef = useRef(false);
  // Explicit process / template-select should replace an existing draft.
  const forceRedraftRef = useRef(false);
  // Template id chosen in this tick — used so draft payload sees the new template
  // before React state settles.
  const pendingTemplateIdRef = useRef<string | null>(null);
  // Populate the saved email from the opinion only once per open, and never
  // over a freshly drafted/edited email in the same session.
  const populatedSavedEmailRef = useRef(false);

  const { data: savedSelection, isFetched: isSelectionFetched } = useQuery({
    queryKey: ['valuation-report-selection', valuationId],
    queryFn: () => getReportSelection(valuationId as string),
    enabled: open && Boolean(valuationId),
  });

  const { data: latestExtractionRun } = useQuery({
    queryKey: ['latest-valuation-extraction-run', valuationId],
    queryFn: () => getLatestValuationExtractionRun(valuationId as string),
    enabled: open && Boolean(valuationId),
    // After extraction completes, CMD/opinion linkage can lag the import callback — keep polling.
    refetchInterval: query => {
      if (!open || !extractionRunId) {
        return false;
      }
      const run = query.state.data;
      const runLinked = run?.run_id === extractionRunId && Boolean(run?.cmd_id) && Boolean(run?.scalar_opinion_id);
      return runLinked ? false : 3000;
    },
  });

  const effectiveCmdId = cmdId ?? latestExtractionRun?.cmd_id ?? undefined;

  // The drafted email lives on the ScalarOpinion produced by the extraction run.
  // For standalone extractions that CMD can differ from the Attio-linked CMD used
  // for contacts/files, so prefer the extraction run's CMD for the email opinion
  // — otherwise a saved draft is written to and read from mismatched opinions.
  const opinionCmdId = latestExtractionRun?.cmd_id ?? cmdId ?? undefined;

  // Load the persisted opinion so a previously saved email can prefill the editor.
  const { data: savedOpinion } = useQuery({
    queryKey: ['scalar-opinion', opinionCmdId],
    queryFn: () => findScalarOpinion(opinionCmdId as number),
    enabled: open && Boolean(opinionCmdId),
  });

  // Prefill the editor with the saved email once per open. Skip once an
  // extraction has started this session so a fresh draft is never overwritten.
  useEffect(() => {
    if (!open || populatedSavedEmailRef.current || extractionRunId || processInitiated || draftEmail) {
      return;
    }
    const saved = savedOpinion?.drafted_email;
    if (saved) {
      const { subject, body } = parseCombinedEmail(saved);
      setDraftSubject(subject);
      setDraftBody(body);
      populatedSavedEmailRef.current = true;
    }
  }, [open, savedOpinion?.drafted_email, extractionRunId, processInitiated, draftEmail]);

  // Persist the current email onto the opinion so it survives modal reopen.
  const { mutate: persistDraftedEmail } = useMutation({
    mutationFn: ({ persistCmdId, email }: { persistCmdId: number; email: string }) =>
      updateScalarOpinion(persistCmdId, { drafted_email: email }),
    onError: error => Logger.error('Failed to save drafted email:', error),
  });

  // Debounce writes while the analyst is typing so we save once they pause
  // instead of on every keystroke.
  const debouncedPersistDraftedEmail = useMemo(
    () =>
      debounce((persistCmdId: number, email: string) => {
        persistDraftedEmail({ persistCmdId, email });
      }, DRAFT_EMAIL_SAVE_DEBOUNCE_MS),
    [persistDraftedEmail]
  );

  // Cancel any pending debounced save on unmount to avoid a late write.
  useEffect(() => () => debouncedPersistDraftedEmail.cancel(), [debouncedPersistDraftedEmail]);

  // Edit handlers: update the field and schedule a debounced save of the
  // recombined subject + body.
  const scheduleEmailSave = useCallback(
    (subject: string, body: string) => {
      if (opinionCmdId) {
        debouncedPersistDraftedEmail(opinionCmdId, formatCombinedEmail(subject, body));
      }
    },
    [opinionCmdId, debouncedPersistDraftedEmail]
  );

  const handleSubjectChange = useCallback(
    (subject: string) => {
      setDraftSubject(subject);
      scheduleEmailSave(subject, draftBody);
    },
    [draftBody, scheduleEmailSave]
  );

  const handleBodyChange = useCallback(
    (body: string) => {
      setDraftBody(body);
      scheduleEmailSave(draftSubject, body);
    },
    [draftSubject, scheduleEmailSave]
  );

  // Flush immediately (on blur) so the last edit is not left pending.
  const saveDraftedEmail = useCallback(() => {
    if (!opinionCmdId) {
      return;
    }
    debouncedPersistDraftedEmail.cancel();
    persistDraftedEmail({ persistCmdId: opinionCmdId, email: draftEmail });
  }, [opinionCmdId, draftEmail, persistDraftedEmail, debouncedPersistDraftedEmail]);

  const extractionDashboardUrl = useMemo(() => {
    if (!latestExtractionRun?.company_slug) {
      return null;
    }
    return dashboardCompanyOverviewUrl(latestExtractionRun.company_slug);
  }, [latestExtractionRun?.company_slug]);

  // Prefer the URL returned with the email draft (deterministic from CMD) over the
  // extraction-run link, which is only available after import completes.
  const dashboardOpinionUrl = draftDashboardUrl || extractionDashboardUrl;

  const { mutate: persistSelection } = useMutation({
    mutationFn: (payload: { model_reference: string | null; report_reference: string | null }) =>
      saveReportSelection(valuationId as string, payload),
    onError: error => Logger.error('Failed to save report selection:', error),
  });

  // When no valuation folder is linked on company/CMD, browse the backup bucket
  // via WorkboardDropboxPicker. Linked folders still list from the same S3 backup
  // (live Dropbox OAuth is not required for Process Report file selection).
  const useS3Fallback = !valuationFolderPath;

  const valuationRelativePrefix = useMemo(
    () => (valuationFolderPath ? toWorkboardRelativePrefix(valuationFolderPath) : ''),
    [valuationFolderPath]
  );

  // The linked folder carries its own valuation root, so listings must be scoped to
  // it rather than assuming the default root.
  const dropboxRoot = useMemo(() => dropboxRootFromReference(valuationFolderPath), [valuationFolderPath]);

  const modelFolderPath = useMemo(
    () => (valuationFolderPath ? buildDropboxPath(valuationFolderPath, MODEL_SUBFOLDER) : null),
    [valuationFolderPath]
  );
  const reportFolderPath = useMemo(
    () => (valuationFolderPath ? buildDropboxPath(valuationFolderPath, REPORT_SUBFOLDER) : null),
    [valuationFolderPath]
  );

  const modelS3Prefix = useMemo(
    () => (valuationRelativePrefix ? `${valuationRelativePrefix}${MODEL_SUBFOLDER}/` : ''),
    [valuationRelativePrefix]
  );
  const reportS3Prefix = useMemo(
    () => (valuationRelativePrefix ? `${valuationRelativePrefix}${REPORT_SUBFOLDER}/` : ''),
    [valuationRelativePrefix]
  );

  const { data: modelFiles = [], isLoading: isLoadingModelFiles } = useQuery({
    queryKey: ['process-report-model-files', dropboxRoot, modelS3Prefix],
    queryFn: () => listWorkboardFiles({ prefix: modelS3Prefix, kind: 'model', root: dropboxRoot }),
    enabled: open && Boolean(modelS3Prefix),
  });

  const { data: reportFiles = [], isLoading: isLoadingReportFiles } = useQuery({
    queryKey: ['process-report-report-files', dropboxRoot, reportS3Prefix],
    queryFn: () => listWorkboardFiles({ prefix: reportS3Prefix, kind: 'report', root: dropboxRoot }),
    enabled: open && Boolean(reportS3Prefix),
  });

  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ['process-report-contacts', effectiveCmdId],
    queryFn: () => getReportContacts(effectiveCmdId as number),
    enabled: open && Boolean(effectiveCmdId),
  });

  const modelCheckboxFiles = useMemo<FileCheckboxItem[]>(
    () =>
      modelFiles.map(file => ({
        id: file.key,
        name: file.name,
        path: file.key,
      })),
    [modelFiles]
  );
  const reportCheckboxFiles = useMemo<FileCheckboxItem[]>(
    () =>
      reportFiles.map(file => ({
        id: file.key,
        name: file.name,
        path: file.key,
      })),
    [reportFiles]
  );

  // Reset transient draft state when the modal is reopened.
  useEffect(() => {
    if (open) {
      setSelectedContactKey('');
      setDraftSubject('');
      setDraftBody('');
      setDraftDashboardUrl(null);
      setNotesParsed(null);
      setExtractionRunId(null);
      setProcessInitiated(false);
      draftInitiatedRef.current = false;
      populatedSavedEmailRef.current = false;
    }
  }, [open]);

  // Hydrate remembered selections (once per open) and reopen the picker at the
  // previously navigated folder.
  useEffect(() => {
    if (!open) {
      hydratedRef.current = false;
      return;
    }
    if (isSelectionFetched && !hydratedRef.current) {
      const modelKey = savedSelection?.model_reference ?? null;
      const reportKey = savedSelection?.report_reference ?? existingReportReference ?? null;
      setSelectedModelPath(modelKey);
      setSelectedReportPath(reportKey);
      setInitialPickerPrefix(deriveDatePrefix(reportKey ?? modelKey));
      lastSavedRef.current = JSON.stringify({ modelKey, reportKey });
      hydratedRef.current = true;
    }
  }, [open, isSelectionFetched, savedSelection, existingReportReference]);

  // Persist selections to the server whenever they change after hydration.
  useEffect(() => {
    if (!open || !hydratedRef.current || !valuationId) {
      return;
    }
    const serialized = JSON.stringify({
      modelKey: selectedModelPath,
      reportKey: selectedReportPath,
    });
    if (serialized !== lastSavedRef.current) {
      lastSavedRef.current = serialized;
      persistSelection({
        model_reference: selectedModelPath,
        report_reference: selectedReportPath,
      });
    }
  }, [open, valuationId, selectedModelPath, selectedReportPath, persistSelection]);

  const selectedModelName = fileNameFromPath(selectedModelPath);
  const selectedReportName = fileNameFromPath(selectedReportPath);

  const selectedContact = useMemo(
    () => contacts.find((contact, index) => contactSelectKey(contact, index) === selectedContactKey) ?? null,
    [contacts, selectedContactKey]
  );

  // Keep selection valid when contacts reload; auto-pick the first contact once available.
  useEffect(() => {
    if (!open || contacts.length === 0) {
      return;
    }
    const keys = contacts.map((contact, index) => contactSelectKey(contact, index));
    if (selectedContactKey && keys.includes(selectedContactKey)) {
      return;
    }
    setSelectedContactKey(keys[0] ?? '');
  }, [open, contacts, selectedContactKey]);

  const persistReportReference = useCallback(
    async (reportCmdId: number) => {
      if (!selectedReportPath || selectedReportPath === existingReportReference) {
        return;
      }
      try {
        await updateScalarOpinion(reportCmdId, { final_report_pdf_reference: selectedReportPath });
      } catch (error) {
        Logger.error('Failed to persist final report reference:', error);
      }
    },
    [selectedReportPath, existingReportReference]
  );

  const resolveTemplateForDraft = useCallback(
    (templateId?: string | null): FinalReportEmailTemplate | null => {
      const targetId = templateId || selectedTemplateId;
      // Prefer the live selected template when it matches — includes Configure edits.
      if (selectedTemplate?.id === targetId) {
        return selectedTemplate;
      }
      return emailTemplates.find(template => template.id === targetId) ?? selectedTemplate ?? null;
    },
    [emailTemplates, selectedTemplate, selectedTemplateId]
  );

  const buildDraftPayload = useCallback(
    (template: FinalReportEmailTemplate) => ({
      model_filename: selectedModelName,
      report_filename: selectedReportName,
      contact_name: selectedContact?.name,
      contact_email: selectedContact?.email,
      subject_template: template.emailSubject,
      introduction_template: template.introduction,
      // Backend still composes for persistence/URL; the editor uses client compose.
      use_llm: false,
    }),
    [
      selectedModelName,
      selectedReportName,
      selectedContact?.name,
      selectedContact?.email,
    ]
  );

  const { mutate: runDraft, isPending: isDrafting } = useMutation({
    mutationFn: async ({
      draftCmdId,
      templateId,
    }: {
      draftCmdId: number;
      templateId?: string | null;
    }) => {
      const template = resolveTemplateForDraft(templateId);
      if (!template) {
        throw new Error('No final report email template selected');
      }
      await persistReportReference(draftCmdId);
      // Fetch deterministic dashboard URL (and keep backend draft in sync).
      const result = await draftFinalReportEmail(draftCmdId, buildDraftPayload(template));
      // Compose locally so the editor matches the selected template exactly
      // (same pattern as onboarding email).
      const combinedEmail = composeFinalReportEmail({
        template,
        companyName,
        contactName: selectedContact?.name,
        reportFilename: selectedReportName,
        dashboardUrl: result.dashboard_url,
      });
      await updateScalarOpinion(draftCmdId, { drafted_email: combinedEmail });
      return {
        combinedEmail,
        dashboardUrl: result.dashboard_url || null,
        notesParsed: result.notes_parsed,
        draftCmdId,
      };
    },
    onSuccess: result => {
      pendingTemplateIdRef.current = null;
      forceRedraftRef.current = false;
      const { subject, body } = parseCombinedEmail(result.combinedEmail);
      setDraftSubject(subject);
      setDraftBody(body);
      setDraftDashboardUrl(result.dashboardUrl);
      setNotesParsed(result.notesParsed);
    },
    onError: error => {
      Logger.error('Failed to draft final report email:', error);
      enqueueSnackbar('Failed to draft final report email. Please try again.', { variant: 'error' });
      // Allow a retry for the same run after a draft failure.
      draftInitiatedRef.current = false;
      forceRedraftRef.current = false;
    },
  });

  const { mutate: runExtraction, isPending: isStartingExtraction } = useMutation({
    mutationFn: async () => {
      if (!valuationId) {
        throw new Error('Missing valuation');
      }
      // Persist the latest selection so the backend reads the current refs.
      await saveReportSelection(valuationId, {
        model_reference: selectedModelPath,
        report_reference: selectedReportPath,
      });
      return startValuationExtraction(valuationId);
    },
    onSuccess: result => {
      const runId = result.run_id ?? result.exec_id ?? null;
      setExtractionRunId(runId);
      // Note: the draft runs in parallel (see runProcess) and must not be
      // cleared here, otherwise the just-drafted email would be wiped when the
      // extraction-start response resolves.
      if (valuationId) {
        queryClient.invalidateQueries({ queryKey: ['latest-valuation-extraction-run', valuationId] });
        // ensure_standalone_company may have just created the CMD for this valuation.
        queryClient.invalidateQueries({
          queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
        });
      }
    },
    onError: (error: Error) => {
      Logger.error('Failed to start valuation extraction:', error);
      enqueueSnackbar(error.message || 'Failed to start valuation extraction. Please try again.', {
        variant: 'error',
      });
    },
  });

  const {
    data: extractionStatus,
    isError: isExtractionStatusError,
    error: extractionStatusError,
  } = useQuery({
    queryKey: ['valuation-extraction-status', extractionRunId],
    queryFn: () => getValuationExtractionStatus(extractionRunId as string),
    enabled: open && Boolean(extractionRunId),
    refetchInterval: query => (isTerminalExtractionStatus(query.state.data?.status) ? false : 5000),
    retry: false,
  });

  useEffect(() => {
    if (
      open &&
      valuationId &&
      isTerminalExtractionStatus(extractionStatus?.status) &&
      extractionStatus?.status?.toLowerCase() === 'completed'
    ) {
      queryClient.invalidateQueries({ queryKey: ['latest-valuation-extraction-run', valuationId] });
      queryClient.invalidateQueries({
        queryKey: ['company-measurement-date-by-attio-valuation-id', valuationId],
      });
    }
  }, [open, valuationId, extractionStatus?.status, queryClient]);

  const isExtractionProcessing =
    Boolean(extractionRunId) && !isExtractionStatusError && !isTerminalExtractionStatus(extractionStatus?.status);

  const isExtractionFailed = Boolean(extractionRunId) && extractionStatus?.status?.toLowerCase() === 'failed';

  // Draft the email as soon as an opinion CMD is available — the dashboard URL
  // is deterministic (company slug), so we do not wait for the async extraction
  // to finish. For a standalone valuation the CMD is created synchronously when
  // extraction starts, so `opinionCmdId` settles shortly after `runProcess`.
  useEffect(() => {
    if (!open || !processInitiated || isDrafting || draftInitiatedRef.current) {
      return;
    }
    // Keep an existing draft unless the analyst explicitly re-ran process or
    // selected a new template (forceRedraftRef).
    if (draftEmail && !forceRedraftRef.current) {
      draftInitiatedRef.current = true;
      return;
    }
    if (!opinionCmdId) {
      return;
    }
    draftInitiatedRef.current = true;
    // Client-side template compose (matches selected template exactly).
    runDraft({
      draftCmdId: opinionCmdId,
      templateId: pendingTemplateIdRef.current ?? selectedTemplateId,
    });
  }, [
    open,
    processInitiated,
    opinionCmdId,
    isDrafting,
    draftEmail,
    runDraft,
    selectedTemplateId,
  ]);

  // Kick off extraction and drafting together so the analyst never waits on the
  // (async) valuation processing to see the drafted email.
  const runProcess = useCallback(() => {
    draftInitiatedRef.current = false;
    forceRedraftRef.current = true;
    setProcessInitiated(true);
    runExtraction();
  }, [runExtraction]);

  const isPipelineBusy = isStartingExtraction || isExtractionProcessing || isDrafting;

  // Model is required to start extraction; the PDF report is optional.
  const canProcess =
    Boolean(valuationId) && Boolean(selectedModelPath) && !isPipelineBusy;

  // Template selection only regenerates the email — never restarts report processing.
  const handleSelectTemplateId = useCallback(
    (id: string) => {
      if (id === selectedTemplateId) {
        return;
      }
      pendingTemplateIdRef.current = id;
      setSelectedTemplateId(id);

      if (isDrafting) {
        return;
      }

      if (opinionCmdId) {
        // Latch so an in-flight process effect does not also draft.
        draftInitiatedRef.current = true;
        runDraft({ draftCmdId: opinionCmdId, templateId: id });
        return;
      }

      // No opinion CMD yet — update the editor from the template only.
      const template = resolveTemplateForDraft(id);
      if (!template) {
        return;
      }
      const combinedEmail = composeFinalReportEmail({
        template,
        companyName,
        contactName: selectedContact?.name,
        reportFilename: selectedReportName,
        dashboardUrl: draftDashboardUrl,
      });
      const { subject, body } = parseCombinedEmail(combinedEmail);
      setDraftSubject(subject);
      setDraftBody(body);
      pendingTemplateIdRef.current = null;
    },
    [
      selectedTemplateId,
      setSelectedTemplateId,
      isDrafting,
      opinionCmdId,
      runDraft,
      resolveTemplateForDraft,
      companyName,
      selectedContact?.name,
      selectedReportName,
      draftDashboardUrl,
    ]
  );

  const handleCopyEmail = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(draftEmail);
      enqueueSnackbar('Email copied to clipboard', { variant: 'success' });
    } catch (error) {
      Logger.error('Failed to copy email:', error);
      enqueueSnackbar('Failed to copy email to clipboard.', { variant: 'error' });
    }
  }, [draftEmail, enqueueSnackbar]);

  let processButtonLabel = 'Start Report Processing';
  if (isStartingExtraction) {
    processButtonLabel = 'Starting…';
  } else if (isExtractionProcessing) {
    processButtonLabel = 'Processing valuation…';
  } else if (isDrafting) {
    processButtonLabel = 'Drafting email…';
  }

  let pipelineStatusColor: 'error.main' | 'secondary.main' | 'text.secondary' = 'text.secondary';
  if (isExtractionStatusError || isExtractionFailed) {
    pipelineStatusColor = 'error.main';
  } else if (isExtractionProcessing || isDrafting) {
    pipelineStatusColor = 'secondary.main';
  }

  let pipelineStatusMessage: string | null = null;
  if (isExtractionStatusError) {
    pipelineStatusMessage =
      extractionStatusError instanceof Error
        ? extractionStatusError.message
        : 'Failed to fetch valuation extraction status.';
  } else if (isExtractionFailed) {
    pipelineStatusMessage = 'Valuation extraction failed. Adjust files and try again.';
  } else if (isStartingExtraction) {
    pipelineStatusMessage = 'Starting valuation extraction…';
  } else if (isExtractionProcessing) {
    pipelineStatusMessage = '… reprocessing … estimated 5 minutes to finish processing …';
  } else if (isDrafting) {
    pipelineStatusMessage = 'Drafting final report email…';
  }

  const inputsSummary = useMemo(() => {
    const source = 'backup bucket';
    const parts: string[] = [];
    parts.push(selectedModelName ? `${selectedModelName} model file from ${source}` : 'No model file selected');
    parts.push(selectedReportName ? `${selectedReportName} report file from ${source}` : 'No report file selected');
    if (notesParsed !== null) {
      parts.push(`${notesParsed} recent ${notesParsed === 1 ? 'note' : 'notes'} parsed`);
    }
    return parts.join(', ');
  }, [selectedModelName, selectedReportName, notesParsed]);

  return {
    companyName,
    selectedModelPath,
    setSelectedModelPath,
    selectedReportPath,
    setSelectedReportPath,
    selectedContactKey,
    setSelectedContactKey,
    emailTemplates,
    emailPresets,
    selectedTemplateId,
    setSelectedTemplateId: handleSelectTemplateId,
    selectedTemplate,
    emailSubject: selectedTemplate?.emailSubject ?? '',
    introduction: selectedTemplate?.introduction ?? '',
    setEmailSubject,
    setIntroduction,
    savePresetOpen,
    presetName,
    setPresetName,
    handleOpenSavePreset,
    handleCloseSavePreset,
    handleSavePreset,
    handleApplyPreset,
    handleDeletePreset,
    handleResetToDefault,
    draftEmail,
    draftSubject,
    handleSubjectChange,
    draftBody,
    handleBodyChange,
    saveDraftedEmail,
    draftDashboardUrl,
    initialPickerPrefix,
    useS3Fallback,
    modelFolderPath,
    reportFolderPath,
    modelCheckboxFiles,
    reportCheckboxFiles,
    isLoadingModelFiles,
    isLoadingReportFiles,
    contacts,
    isLoadingContacts,
    latestExtractionRun,
    dashboardOpinionUrl,
    runProcess,
    canProcess,
    processButtonLabel,
    isPipelineBusy,
    isStartingExtraction,
    isExtractionProcessing,
    isDrafting,
    isExtractionStatusError,
    isExtractionFailed,
    pipelineStatusColor,
    pipelineStatusMessage,
    inputsSummary,
    handleCopyEmail,
  };
};

export default useProcessReportModal;
